import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { authSettingsApi, type AuthSettings } from '../api/authSettings';

interface AuthSettingsState {
  settings: AuthSettings | null;
  loading: boolean;
  error: string | null;
}

interface AuthSettingsContextType extends AuthSettingsState {
  refreshSettings: () => Promise<void>;
  isLocalPasswordEnabled: boolean;
}

const initialState: AuthSettingsState = {
  settings: null,
  loading: true,
  error: null,
};

type AuthSettingsAction =
  | { type: 'LOADING_START' }
  | { type: 'LOADING_SUCCESS'; payload: AuthSettings }
  | { type: 'LOADING_ERROR'; payload: string }
  | { type: 'RESET_ERROR' };

function authSettingsReducer(state: AuthSettingsState, action: AuthSettingsAction): AuthSettingsState {
  switch (action.type) {
    case 'LOADING_START':
      return { ...state, loading: true, error: null };
    case 'LOADING_SUCCESS':
      return { ...state, loading: false, settings: action.payload, error: null };
    case 'LOADING_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'RESET_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

const AuthSettingsContext = createContext<AuthSettingsContextType | undefined>(undefined);

export function AuthSettingsProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authSettingsReducer, initialState);
  const isLoadingSettings = useRef(false);

  const refreshSettings = async () => {
    // Prevent multiple simultaneous settings loads
    if (isLoadingSettings.current) {
      return;
    }

    isLoadingSettings.current = true;
    
    try {
      dispatch({ type: 'LOADING_START' });
      console.log('AuthSettingsContext: Starting to load settings...');
      
      // Always use public endpoint for basic authentication status
      const publicSettings = await authSettingsApi.getPublicSettings();
      console.log('AuthSettingsContext: Public settings loaded:', publicSettings);
      
      // Create a minimal AuthSettings object for UI compatibility
      const settings: AuthSettings = {
        local_password_enabled: publicSettings.local_password_enabled,
        ms_ad_enabled: publicSettings.ms_ad_enabled,
        updated_at: new Date().toISOString()
      };
      
      dispatch({ type: 'LOADING_SUCCESS', payload: settings });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load auth settings';
      console.error('AuthSettingsContext: Failed to load settings:', error);
      dispatch({ type: 'LOADING_ERROR', payload: errorMessage });
    } finally {
      isLoadingSettings.current = false;
    }
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const value: AuthSettingsContextType = {
    ...state,
    refreshSettings,
    isLocalPasswordEnabled: state.error ? true : (state.settings?.local_password_enabled ?? true), // Default to true if error or no settings
  };

  console.log('AuthSettingsContext: Current state:', {
    loading: state.loading,
    error: state.error,
    settings: state.settings,
    isLocalPasswordEnabled: value.isLocalPasswordEnabled
  });

  return <AuthSettingsContext.Provider value={value}>{children}</AuthSettingsContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuthSettings = () => {
  const context = useContext(AuthSettingsContext);
  if (context === undefined) {
    throw new Error('useAuthSettings must be used within an AuthSettingsProvider');
  }
  return context;
};